import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { payWithRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import {
  Loader2, Calculator, Layers, Sparkles, ShieldCheck, FileDown, FileSpreadsheet,
  Lock, Save, TrendingUp, CheckCircle2, AlertCircle, Building2, ArrowRight, Wand2
} from "lucide-react";

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const compact = (n) => n >= 1e7 ? `₹${(n / 1e7).toFixed(2)} Cr` : n >= 1e5 ? `₹${(n / 1e5).toFixed(1)} L` : inr(n);
const AUTHORITIES = ["DDA", "MCD", "Noida Authority", "Greater Noida Authority", "YEIDA", "Ghaziabad Development Authority", "HUDA", "Private Freehold", "Other"];
const PLOT_TYPES = ["Freehold", "Authority Plot", "Society Plot", "Government Approved"];
const CONSTR_TYPES = { Residential: ["Independent House", "Villa", "Duplex", "Farm House", "Builder Floor", "MRU", "PG", "Row House"], Commercial: ["Office", "Retail Shop", "Hall", "Showroom", "Hospital", "School", "Warehouse", "Factory", "Hotel", "Restaurant", "Guest House", "Mixed Use"], Industrial: ["Factory", "Warehouse"], Institutional: ["School", "Hospital"] };
const SCOPES = ["Structure Only", "Grey Structure", "Move In", "Basic Interior", "Premium Interior", "Luxury Interior", "Ultra Luxury"];

export default function EstimateStudio() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [projects, setProjects] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [confidence, setConfidence] = useState(null);
  const [f, setF] = useState({
    project_name: "", location: "", state: "", city: "", authority: "DDA", plot_type: "Freehold",
    plot_area: 1800, ground_coverage: 60, floors: 2, area_per_floor: 1080, mumty_area: 100,
    basement: "NO", basement_area: 0, constr_type: "Residential", sub_type: "Independent House",
    scope: "Move In", base_rate_id: "standard", foundation_depth: 6, plinth_ft: 1.5,
  });
  const [sel, setSel] = useState({});
  const [savedId, setSavedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rec, setRec] = useState({ loading: false, data: null });
  const [boq, setBoq] = useState(null);
  const [boqLoading, setBoqLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [coupon, setCoupon] = useState({ code: "", status: null, applying: false });

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    api.get("/estimate/config").then((r) => {
      setConfig(r.data);
      setF((s) => ({ ...s, base_rate_id: r.data.base_rates[0]?.id || "" }));
      const init = {}; r.data.packages.forEach((p) => { init[p.id] = 0; }); setSel(init);
    });
    api.get("/projects").then((r) => {
      setProjects(r.data);
      if (r.data.length && !params.get("boq")) setProjectId(r.data[0].id);
    }).catch(() => setProjects([]));
    api.get("/estimate/boq/coupon").then((r) => setCoupon((c) => ({ ...c, status: r.data }))).catch(() => {});
  }, []); // eslint-disable-line

  // Prefill from selected project + load confidence
  useEffect(() => {
    if (!projectId) { setConfidence(null); return; }
    const p = (projects || []).find((x) => x.id === projectId);
    if (p) {
      setF((s) => ({
        ...s,
        project_name: p.name || s.project_name,
        city: p.city || s.city,
        plot_area: p.plot_area || s.plot_area,
        floors: p.floors || s.floors,
      }));
    }
    api.get(`/estimate/confidence/${projectId}`).then((r) => setConfidence(r.data)).catch(() => setConfidence(null));
  }, [projectId, projects]);

  // Any change invalidates saved snapshot
  useEffect(() => { setSavedId(null); }, [f, sel]);

  const calc = useMemo(() => {
    if (!config) return null;
    const rate = config.base_rates.find((r) => r.id === f.base_rate_id) || config.base_rates[0];
    const bua = Number(f.area_per_floor) * Number(f.floors) + Number(f.mumty_area || 0);
    const gca = Number(f.plot_area) * Number(f.ground_coverage) / 100;
    const baseTotal = (rate?.rate || 0) * bua;
    const pkgs = config.packages.map((p) => {
      const idx = sel[p.id] ?? 0;
      const up = p.upgrades[idx] || p.upgrades[0];
      const baseCost = baseTotal * p.pct / 100;
      const current = baseCost * (up?.multiplier || 1);
      return { id: p.id, name: p.name, baseCost, current, additional: current - baseCost, upgrade: up?.label };
    });
    const upgradeTotal = pkgs.reduce((a, p) => a + p.additional, 0);
    const fd = config.foundation, pl = config.plinth;
    const foundationAdj = Math.max(0, Number(f.foundation_depth) - fd.default_depth_ft) * fd.extra_rate_per_ft_per_sqft * gca;
    const plinthAdj = Math.max(0, Number(f.plinth_ft) - pl.default_ft) * pl.extra_rate_per_ft_per_sqft * gca;
    const basementCost = f.basement === "YES" ? Number(f.basement_area || 0) * config.basement.rate_per_sqft : 0;
    const total = baseTotal + upgradeTotal + foundationAdj + plinthAdj + basementCost;
    return { rate, bua, gca, baseTotal, pkgs, upgradeTotal, foundationAdj, plinthAdj, basementCost, total, fd, pl };
  }, [config, f, sel]);

  const options = useMemo(() => {
    if (!config || !calc) return null;
    const adj = calc.foundationAdj + calc.plinthAdj + calc.basementCost;
    const build = (pick) => calc.baseTotal + config.packages.reduce((a, p) => {
      const base = calc.baseTotal * p.pct / 100;
      const mults = p.upgrades.map((u) => u.multiplier).filter((m) => m > 0);
      const idx = sel[p.id] ?? 0;
      const m = pick === "smart" ? Math.min(...mults) : pick === "premium" ? Math.max(...mults) : (p.upgrades[idx]?.multiplier ?? 1);
      return a + base * (m - 1);
    }, 0) + adj;
    return { smart: build("smart"), recommended: build("rec"), premium: build("premium") };
  }, [config, calc, sel]);

  const snapshot = useCallback(() => ({
    project_id: projectId,
    inputs: { ...f, bua: calc.bua, gca: calc.gca, rate_name: calc.rate?.name, rate: calc.rate?.rate },
    selections: calc.pkgs.map((p) => ({ package: p.name, brand: p.upgrade, base: p.baseCost, current: p.current, additional: p.additional })),
    totals: { baseTotal: calc.baseTotal, upgradeTotal: calc.upgradeTotal, foundationAdj: calc.foundationAdj, plinthAdj: calc.plinthAdj, basementCost: calc.basementCost, total: calc.total },
    options,
  }), [projectId, f, calc, options]);

  const ensureSaved = useCallback(async () => {
    if (savedId) return savedId;
    if (!projectId) { toast.error("Please select a project first"); return null; }
    const { data } = await api.post("/estimate/save", snapshot());
    setSavedId(data.id);
    return data.id;
  }, [savedId, projectId, snapshot]);

  const doSave = async () => {
    if (!projectId) { toast.error("Select a project to save this estimate"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/estimate/save", snapshot());
      setSavedId(data.id);
      toast.success("Estimate saved to your project dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save estimate"); }
    finally { setSaving(false); }
  };

  const runRecommendation = async () => {
    setRec({ loading: true, data: null });
    try {
      const id = await ensureSaved();
      const { data } = await api.post("/estimate/recommendation", {
        selections: snapshot().selections, total: calc.total, base_total: calc.baseTotal, estimate_id: id,
      });
      setRec({ loading: false, data });
    } catch { setRec({ loading: false, data: null }); toast.error("AI analysis failed. Try again."); }
  };

  const download = async (kind, fmt) => {
    const key = `${kind}-${fmt}`;
    setDownloading(key);
    try {
      const id = kind === "boq" ? savedId : await ensureSaved();
      if (!id) return;
      const res = await api.get(`/estimate/${id}/download?kind=${kind}&fmt=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${(f.project_name || "estimate").replace(/\s+/g, "_")}_${kind}.${fmt === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    finally { setDownloading(""); }
  };

  const unlockBoq = async () => {
    try {
      const id = await ensureSaved();
      if (!id) return;
      await payWithRazorpay({
        purpose: "boq", estimate_id: id, description: "Detailed BOQ (₹199)",
        onSuccess: () => { toast.success("Payment successful!"); generateBoq(id); },
        onFailure: (e) => { if (e?.message !== "cancelled") toast.error(e?.response?.data?.detail || "Payment could not be completed"); },
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); }
  };

  const applyCoupon = async () => {
    if (!coupon.code.trim()) { toast.error("Enter a coupon code"); return; }
    setCoupon((c) => ({ ...c, applying: true }));
    try {
      const id = await ensureSaved();
      if (!id) { setCoupon((c) => ({ ...c, applying: false })); return; }
      const { data } = await api.post("/estimate/boq/apply-coupon", { estimate_id: id, code: coupon.code.trim() });
      toast.success("Coupon applied — BOQ unlocked for free!");
      if (typeof data.remaining === "number") setCoupon((c) => ({ ...c, applying: false, status: { ...(c.status || {}), remaining: data.remaining } }));
      await generateBoq(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply coupon");
    } finally {
      setCoupon((c) => ({ ...c, applying: false }));
    }
  };

  const generateBoq = useCallback(async (id) => {
    setBoqLoading(true);
    try {
      const { data } = await api.post("/estimate/boq/generate", { estimate_id: id });
      setBoq(data); setSavedId(id);
      toast.success("Your AI Bill of Quantities is ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "BOQ generation failed"); }
    finally { setBoqLoading(false); }
  }, []);

  // Handle return from BOQ payment
  useEffect(() => {
    const b = params.get("boq");
    if (b) {
      generateBoq(b);
      params.delete("boq"); setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line

  if (authLoading || !config || !calc || projects === null) {
    return <div className="min-h-screen bg-[#FBFAF9] flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div>;
  }

  if (projects.length === 0) {
    return (
      <div className="min-h-screen bg-[#FBFAF9]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="card-premium rounded-2xl p-12" data-testid="es-no-project">
            <Building2 className="h-10 w-10 text-[#0E9AC8] mx-auto mb-4" strokeWidth={1.25} />
            <h1 className="font-display text-2xl font-extrabold text-slate-900">Create a project to start estimating</h1>
            <p className="mt-2 text-slate-500">The Estimate Studio ties every estimate to a project so you can save it, track confidence, and generate a BOQ.</p>
            <Button data-testid="es-create-project" onClick={() => navigate("/dashboard")} className="mt-6 bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl">
              Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const optionData = options ? [
    { name: "Smart Budget", value: Math.round(options.smart), fill: "#16a34a" },
    { name: "Recommended", value: Math.round(options.recommended), fill: "#0E9AC8" },
    { name: "Premium", value: Math.round(options.premium), fill: "#0F172A" },
  ] : [];
  const pkgChart = [...calc.pkgs].sort((a, b) => b.current - a.current).slice(0, 8).map((p) => ({ name: p.name, value: Math.round(p.current), up: p.additional > 1 }));

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-xl bg-[#0E9AC8] flex items-center justify-center"><Calculator className="h-6 w-6 text-white" /></div>
          <div><h1 className="font-display text-3xl font-extrabold text-slate-900">NCIS Estimate Studio</h1>
            <p className="text-slate-500 text-sm">Material Intelligence engine — upgrade any package, see live impact, and generate a professional estimate.</p></div>
        </div>

        {/* Project selector */}
        <div className="card-premium rounded-2xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <Label className="text-sm text-slate-600 shrink-0 flex items-center gap-1.5"><Building2 className="h-4 w-4 text-[#0E9AC8]" /> Estimating for project</Label>
          <div className="flex-1">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="es-project-select" className="rounded-xl max-w-md"><SelectValue placeholder="Select a project" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.city}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-xl text-sm">+ New Project</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: inputs */}
          <div className="lg:col-span-2 space-y-6">
            <Card title="Project Information">
              <Grid>
                <FF label="Project Name"><Input data-testid="es-name" className="rounded-xl" value={f.project_name} onChange={(e) => setF({ ...f, project_name: e.target.value })} /></FF>
                <FF label="City"><Input className="rounded-xl" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></FF>
                <FF label="State"><Input className="rounded-xl" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} /></FF>
                <FF label="Authority"><Sel v={f.authority} set={(v) => setF({ ...f, authority: v })} opts={AUTHORITIES} /></FF>
                <FF label="Plot Type"><Sel v={f.plot_type} set={(v) => setF({ ...f, plot_type: v })} opts={PLOT_TYPES} /></FF>
                <FF label="Construction Type"><Sel v={f.constr_type} set={(v) => setF({ ...f, constr_type: v, sub_type: (CONSTR_TYPES[v] || [])[0] })} opts={Object.keys(CONSTR_TYPES)} /></FF>
                <FF label="Sub Type"><Sel v={f.sub_type} set={(v) => setF({ ...f, sub_type: v })} opts={CONSTR_TYPES[f.constr_type] || []} /></FF>
                <FF label="Construction Scope"><Sel v={f.scope} set={(v) => setF({ ...f, scope: v })} opts={SCOPES} /></FF>
              </Grid>
              <Grid>
                <FF label="Plot Area (sqft)"><Num v={f.plot_area} set={(v) => setF({ ...f, plot_area: v })} tid="es-plot" /></FF>
                <FF label="Ground Coverage %"><Num v={f.ground_coverage} set={(v) => setF({ ...f, ground_coverage: v })} /></FF>
                <FF label="No. of Floors"><Num v={f.floors} set={(v) => setF({ ...f, floors: v })} tid="es-floors" /></FF>
                <FF label="Area per Floor (sqft)"><Num v={f.area_per_floor} set={(v) => setF({ ...f, area_per_floor: v })} /></FF>
                <FF label="Mumty Area (sqft)"><Num v={f.mumty_area} set={(v) => setF({ ...f, mumty_area: v })} /></FF>
                <FF label="Basement"><Sel v={f.basement} set={(v) => setF({ ...f, basement: v })} opts={["NO", "YES"]} /></FF>
                {f.basement === "YES" && <FF label="Basement Area (sqft)"><Num v={f.basement_area} set={(v) => setF({ ...f, basement_area: v })} tid="es-basement-area" /></FF>}
              </Grid>
              <div className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3 mt-1">
                Built-up Area: <b>{Math.round(calc.bua).toLocaleString("en-IN")} sqft</b> · Ground Coverage Area: <b>{Math.round(calc.gca).toLocaleString("en-IN")} sqft</b>
              </div>
            </Card>

            <Card title="Base Construction Rate">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {config.base_rates.map((r) => (
                  <button key={r.id} data-testid={`es-rate-${r.id}`} onClick={() => setF({ ...f, base_rate_id: r.id })}
                    className={`rounded-xl border p-4 text-left transition-colors ${f.base_rate_id === r.id ? "border-[#0E9AC8] bg-[#0E9AC8]/5" : "border-slate-200 hover:border-[#0E9AC8]"}`}>
                    <p className="font-semibold text-slate-800">{r.name}</p><p className="text-sm text-[#0E9AC8] font-bold">{inr(r.rate)}/sqft</p>
                  </button>
                ))}
              </div>
            </Card>

            <Card title="Foundation, Plinth & Basement">
              <p className="text-xs text-slate-500 mb-3">Assumptions — Foundation depth {calc.fd.default_depth_ft} ft, Plinth {calc.pl.default_ft} ft. Every extra foot adds ₹{calc.fd.extra_rate_per_ft_per_sqft}/sqft of ground coverage.</p>
              <Grid>
                <FF label="Foundation Depth (ft)"><Num v={f.foundation_depth} set={(v) => setF({ ...f, foundation_depth: v })} tid="es-foundation" /></FF>
                <FF label="Plinth Height (ft)"><Num v={f.plinth_ft} set={(v) => setF({ ...f, plinth_ft: v })} /></FF>
              </Grid>
            </Card>

            <Card title="Work Packages — Material Intelligence">
              <p className="text-xs text-slate-500 mb-3">Upgrade any package independently (brand / finish). Only that package recalculates.</p>
              <div className="divide-y divide-slate-100">
                {calc.pkgs.map((p, i) => (
                  <div key={p.id} data-testid={`es-pkg-${i}`} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0"><p className="font-medium text-slate-800 text-sm">{p.name}</p>
                      <p className="text-xs text-slate-400">{inr(p.current)}{p.additional > 1 && <span className="text-[#F5923C]"> (+{inr(p.additional)})</span>}</p></div>
                    <select data-testid={`es-pkg-sel-${i}`} value={sel[p.id] ?? 0} onChange={(e) => setSel({ ...sel, [p.id]: Number(e.target.value) })}
                      className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm shrink-0 max-w-[190px]">
                      {config.packages[i].upgrades.map((u, ui) => <option key={ui} value={ui}>{u.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </Card>

            {/* Graphical cost impact */}
            <Card title="Live Cost Impact">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Comparative Options</p>
                  <div className="h-[220px]" data-testid="es-options-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={optionData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={(v) => compact(v)} tick={{ fontSize: 10 }} width={54} />
                        <Tooltip formatter={(v) => inr(v)} />
                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                          {optionData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                          <LabelList dataKey="value" position="top" formatter={(v) => compact(v)} style={{ fontSize: 10, fill: "#475569" }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Top Package Costs</p>
                  <div className="h-[220px]" data-testid="es-package-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart layout="vertical" data={pkgChart} margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                        <XAxis type="number" tickFormatter={(v) => compact(v)} tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => inr(v)} />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                          {pkgChart.map((d, i) => <Cell key={i} fill={d.up ? "#F5923C" : "#0E9AC8"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </Card>

            {/* 3 option cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="es-options">
              <OptionCard title="Smart Budget" desc="Cheapest brand in every package" value={options?.smart} tone="green" />
              <OptionCard title="Recommended" desc="Your current selection" value={options?.recommended} tone="teal" highlight />
              <OptionCard title="Premium" desc="Top-tier brand in every package" value={options?.premium} tone="dark" />
            </div>

            {/* AI Recommendation */}
            <Card title="AI Recommendation Engine">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-sm text-slate-500">Understand why your cost is where it is — and get value-for-money alternatives.</p>
                <Button data-testid="es-analyze-btn" onClick={runRecommendation} disabled={rec.loading}
                  className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl shrink-0">
                  {rec.loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analysing…</> : <><Wand2 className="h-4 w-4 mr-2" /> Analyse My Estimate</>}
                </Button>
              </div>
              {rec.data && (
                <div className="space-y-4" data-testid="es-recommendation">
                  <p className="text-sm text-slate-700 leading-relaxed bg-[#0E9AC8]/5 border border-[#0E9AC8]/15 rounded-xl p-4">{rec.data.why}</p>
                  {(rec.data.alternatives || []).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Value-for-money alternatives</p>
                      {rec.data.alternatives.map((a, i) => (
                        <div key={i} className="flex items-start gap-3 border-l-2 border-emerald-500 pl-3 py-1">
                          <TrendingUp className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                          <p className="text-sm text-slate-700"><b>{a.package}</b>: {a.from} → <b className="text-emerald-700">{a.to}</b>. {a.note}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {rec.data.verdict && <p className="text-sm font-semibold text-slate-900">{rec.data.verdict}</p>}
                </div>
              )}
            </Card>

            {/* BOQ */}
            <Card title="AI Bill of Quantities (BOQ)">
              {boq ? (
                <div data-testid="es-boq-result">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"><CheckCircle2 className="h-3.5 w-3.5" /> Unlocked</span>
                    <div className="flex gap-2">
                      <Button data-testid="es-boq-pdf" size="sm" variant="outline" onClick={() => download("boq", "pdf")} disabled={downloading === "boq-pdf"} className="rounded-xl">
                        {downloading === "boq-pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileDown className="h-4 w-4 mr-1" /> PDF</>}
                      </Button>
                      <Button data-testid="es-boq-excel" size="sm" variant="outline" onClick={() => download("boq", "excel")} disabled={downloading === "boq-excel"} className="rounded-xl">
                        {downloading === "boq-excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</>}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
                    {(boq.sections || []).map((s, i) => (
                      <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                        <div className="bg-slate-50 px-3 py-2 flex items-center justify-between">
                          <p className="font-semibold text-sm text-slate-800">{s.title}</p>
                          <p className="text-sm font-bold text-[#0E9AC8]">{inr(s.subtotal)}</p>
                        </div>
                        <table className="w-full text-xs">
                          <tbody>
                            {(s.items || []).map((it, j) => (
                              <tr key={j} className="border-t border-slate-100">
                                <td className="px-3 py-1.5 text-slate-700">{it.description}</td>
                                <td className="px-2 py-1.5 text-slate-400 text-right whitespace-nowrap">{it.qty} {it.unit}</td>
                                <td className="px-2 py-1.5 text-slate-400 text-right whitespace-nowrap">@ {inr(it.rate)}</td>
                                <td className="px-3 py-1.5 text-slate-800 text-right font-medium whitespace-nowrap">{inr(it.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-900 text-white rounded-xl">
                      <span className="font-semibold text-sm">Grand Total</span>
                      <span className="font-display text-lg font-extrabold">{inr(boq.grand_total)}</span>
                    </div>
                    {(boq.assumptions || []).length > 0 && (
                      <div className="text-xs text-slate-500">
                        <p className="font-semibold text-slate-600 mb-1">Assumptions</p>
                        <ul className="list-disc pl-4 space-y-0.5">{boq.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6" data-testid="es-boq-locked">
                  <Lock className="h-8 w-8 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
                  <p className="font-semibold text-slate-800">Unlock your detailed AI Bill of Quantities</p>
                  <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Section-wise quantities, units, rates and amounts with clear assumptions — generated by our AI quantity surveyor.</p>
                  {coupon.status?.active && (
                    <div className="mt-4 mx-auto max-w-md rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-left" data-testid="es-boq-offer">
                      <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Launch offer — get your BOQ FREE</p>
                      <p className="text-xs text-emerald-700 mt-0.5">First 100 customers only · <b>{coupon.status.remaining}</b> free unlocks left. Use code <b>{coupon.status.code}</b>.</p>
                      <div className="mt-3 flex gap-2">
                        <Input data-testid="es-coupon-input" value={coupon.code} onChange={(e) => setCoupon({ ...coupon, code: e.target.value.toUpperCase() })}
                          placeholder="Enter coupon code" className="rounded-xl bg-white uppercase" />
                        <Button data-testid="es-coupon-apply" onClick={applyCoupon} disabled={coupon.applying}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shrink-0">
                          {coupon.applying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply & Unlock"}
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="mt-4">
                    <Button data-testid="es-boq-unlock" onClick={unlockBoq} disabled={boqLoading}
                      className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11 px-6">
                      {boqLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-2" /> {coupon.status?.active ? "Or pay ₹199" : "Unlock BOQ · ₹199"}</>}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">One-time · Secure payment via Razorpay · UPI, cards &amp; netbanking</p>
                </div>
              )}
            </Card>
          </div>

          {/* RIGHT: live panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 space-y-4">
              <div className="rounded-2xl bg-slate-900 text-white p-6" data-testid="es-live-panel">
                <p className="text-xs uppercase tracking-wide text-white/60">Total Project Cost</p>
                <p className="font-display text-4xl font-extrabold mt-1" data-testid="es-total">{inr(calc.total)}</p>
                <p className="text-sm text-white/70 mt-1">Current rate: {inr(calc.rate?.rate)}/sqft · {calc.rate?.name}</p>
                <div className="mt-5 space-y-2 text-sm">
                  <Row k="Base Construction" v={inr(calc.baseTotal)} />
                  <Row k="Upgrade Cost" v={`+${inr(calc.upgradeTotal)}`} accent />
                  <Row k="Foundation Adjustment" v={`+${inr(calc.foundationAdj)}`} />
                  <Row k="Plinth Adjustment" v={`+${inr(calc.plinthAdj)}`} />
                  <Row k="Basement Cost" v={`+${inr(calc.basementCost)}`} />
                </div>
              </div>

              {/* Confidence score */}
              <div className="card-premium rounded-2xl p-5" data-testid="es-confidence">
                <p className="font-display font-bold text-slate-900 flex items-center gap-2 mb-3"><ShieldCheck className="h-4 w-4 text-[#0E9AC8]" /> Estimate Confidence</p>
                {confidence ? (
                  <>
                    <div className="flex items-end gap-2">
                      <span className="font-display text-4xl font-extrabold" style={{ color: confidence.score >= 95 ? "#16a34a" : confidence.score >= 80 ? "#0E9AC8" : "#F5923C" }}>{confidence.score}%</span>
                      <span className="text-sm text-slate-500 mb-1">{confidence.band} confidence</span>
                    </div>
                    <div className="mt-3 space-y-1.5 text-sm">
                      <ConfRow ok={confidence.has_architectural} label="Architectural / Floor Plan" />
                      <ConfRow ok={confidence.has_structural} label="Structural Drawing" />
                      <ConfRow ok={confidence.has_soil} label="Soil Report" />
                    </div>
                    {confidence.missing?.length > 0 && (
                      <p className="mt-3 text-xs text-slate-500">Upload {confidence.missing.join(", ")} to your project to raise accuracy.</p>
                    )}
                  </>
                ) : <p className="text-sm text-slate-500">Select a project to compute confidence from your uploaded documents.</p>}
              </div>

              {/* Actions */}
              <div className="card-premium rounded-2xl p-5 space-y-2">
                <Button data-testid="es-save-btn" onClick={doSave} disabled={saving} className="w-full bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> {savedId ? "Saved ✓ — Save Again" : "Save to Project"}</>}
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button data-testid="es-pdf-btn" variant="outline" onClick={() => download("estimate", "pdf")} disabled={downloading === "estimate-pdf"} className="rounded-xl">
                    {downloading === "estimate-pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileDown className="h-4 w-4 mr-1" /> PDF</>}
                  </Button>
                  <Button data-testid="es-excel-btn" variant="outline" onClick={() => download("estimate", "excel")} disabled={downloading === "estimate-excel"} className="rounded-xl">
                    {downloading === "estimate-excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</>}
                  </Button>
                </div>
              </div>

              <div className="card-premium rounded-2xl p-5">
                <p className="font-display font-bold text-slate-900 flex items-center gap-2 mb-3"><Layers className="h-4 w-4 text-[#0E9AC8]" /> Package-wise Cost</p>
                <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
                  {calc.pkgs.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{p.name}</span>
                      <span className="font-semibold text-slate-800">{inr(p.current)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const Card = ({ title, children }) => (<div className="card-premium rounded-2xl p-6"><h3 className="font-display text-lg font-bold text-slate-900 mb-4">{title}</h3><div className="space-y-4">{children}</div></div>);
const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
const FF = ({ label, children }) => <div><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1.5">{children}</div></div>;
const Num = ({ v, set, tid }) => <Input data-testid={tid} type="number" className="rounded-xl" value={v} onChange={(e) => set(e.target.value)} />;
const Sel = ({ v, set, opts }) => (<Select value={v} onValueChange={set}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>);
const Row = ({ k, v, accent }) => (<div className="flex items-center justify-between"><span className="text-white/70">{k}</span><span className={accent ? "text-[#F5923C] font-semibold" : "font-medium"}>{v}</span></div>);
const ConfRow = ({ ok, label }) => (
  <div className="flex items-center gap-2">
    {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertCircle className="h-4 w-4 text-slate-300" />}
    <span className={ok ? "text-slate-700" : "text-slate-400"}>{label}</span>
  </div>
);
const OptionCard = ({ title, desc, value, tone, highlight }) => {
  const tones = { green: "text-emerald-600", teal: "text-[#0E9AC8]", dark: "text-slate-900" };
  return (
    <div className={`card-premium rounded-2xl p-5 ${highlight ? "ring-2 ring-[#0E9AC8]" : ""}`}>
      <div className="flex items-center justify-between">
        <p className="font-semibold text-slate-800">{title}</p>
        {highlight && <span className="text-[10px] font-bold uppercase tracking-wide text-[#0E9AC8] bg-[#0E9AC8]/10 px-2 py-0.5 rounded-full">Best fit</span>}
      </div>
      <p className={`font-display text-2xl font-extrabold mt-2 ${tones[tone]}`}>{compact(value || 0)}</p>
      <p className="text-xs text-slate-400 mt-1">{desc}</p>
    </div>
  );
};
